import { $createBoundTask } from "@captions-web/dev-logger";
import { Icon } from "@captions-web/icons";
import { isAbortOrCanceledError } from "@captions-web/utils/abort-signals";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { t } from "i18next";
import { nanoid } from "nanoid";
import { useCallback, useRef, useState, forwardRef } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { LayoutBleed } from "~/components/layouts/bleed";
import { Button, LinkButton } from "~/components/primitives/button";
import { Dialog } from "~/components/primitives/dialog";
import { ScrollArea } from "~/components/primitives/scroll-area";
import { AdjustAudioClipEvents } from "~/lib/analytics/defs/AdjustAudioClipEvents";
import { useFeatureFlag } from "~/lib/feature-flags/hooks";
import { formatTimeMs } from "~/lib/format-time";
import { useLatestRef, useOnChangeEffect, useOnMountEffect } from "~/lib/hooks/react-helpers";
import { PollingFailedError } from "~/lib/network/polling-helper";
import { AudioProcessor } from "~/modules/audio/audio-processor";
import {
  AudioTrimmer,
  TrimmableRegion,
  TrimmableRegion_Id,
} from "~/modules/audio/components/audio-trimmer/audio-trimmer";
import { MAX_REGION_SIZE_MS, MIN_REGION_SIZE_MS } from "~/modules/audio/constants";
import { ProjectOptionsMenu } from "~/modules/project/components/project-options-menu";
import { ProjectLayout } from "~/modules/project/layouts/project-layout";
import { setClipRangesMutation } from "~/modules/project/network/mutations";
import {
  getProjectDetailsQuery,
  pollAudioClipRangesCreationQuery,
} from "~/modules/project/network/queries";
import {
  pollAudioClipRangesResponseSchema,
  ProjectDetailsType,
} from "~/modules/project/network/schemas";
import { css, cva } from "~/styled-system/css";

export const Route = createFileRoute("/_app/projects/$projectId/$audioId/adjust")({
  component: RouteComponent,
  validateSearch: z.object({
    fromProject: z.coerce.boolean().optional(),
  }),
  async loader({ params: { projectId }, context: { queryClient } }) {
    queryClient.prefetchQuery(getProjectDetailsQuery.queryOptions({ params: { id: projectId } }));
  },
});

function RouteComponent() {
  const { audioId, projectId } = Route.useParams();
  const navigate = useNavigate();

  const [trimmableRegions, setTrimmableRegions] = useState<TrimmableRegion[]>([]);
  const [isContinueClicked, setIsContinueClicked] = useState(false);
  const [currentTimeMs, setCurrentTimeMs] = useState(0);
  const [groupedTranscription, setGroupedTranscription] = useState<TranscriptionItem[]>([
    {
      startMs: 0,
      endMs: 0,
      text: "Splitting audio...",
    },
  ]);
  const latestTrimmableRegions = useLatestRef(trimmableRegions);
  const audioProcessorRef = useRef<AudioProcessor>();

  const {
    begin,
    currentResult,
    isPending: isPollingClipRangeCreation,
  } = pollAudioClipRangesCreationQuery.usePollingQuery({
    swallowAbortErrors: true,

    onError(error) {
      let knownErrorCode: string | undefined;
      if (error instanceof PollingFailedError) {
        const parsedResult = pollAudioClipRangesResponseSchema.safeParse(error.result);
        if (parsedResult.success && parsedResult.data.state === "FAILED") {
          knownErrorCode = parsedResult.data.error?.code;
        }
      }

      AdjustAudioClipEvents.polling_failed_from_client.track({
        project_id: projectId,
        audio_id: audioId,
        error_message: knownErrorCode ?? error.message,
      });
    },
  });

  const { mutateAsync: setClipRanges, isPending: isSettingClipRanges } =
    setClipRangesMutation.useMutation();

  const saveClipRanges = useCallback(() => {
    const regions = latestTrimmableRegions.current;
    const clipRanges = regions.map(({ startMs, endMs }) => ({ startMs, endMs }));
    return setClipRanges({ params: { audioId, projectId, clipRanges } });
    // eslint-disable-next-line
  }, [audioId, setClipRanges, projectId]);

  const onContinue = useCallback(async () => {
    setIsContinueClicked(true);
    if (currentResult?.state !== "done") {
      return;
    }

    try {
      const $task = $createBoundTask({
        trackEvent: AdjustAudioClipEvents.submit_status.track,
        getMetadata: () => ({
          project_id: projectId,
          audio_id: audioId,
          clip_count: trimmableRegions.length,
          audio_duration_ms: currentResult?.result.audioDurationMs,
        }),
      });
      // don't await this, immediately move on to next step
      $task("save-clip-ranges", () => saveClipRanges());

      return navigate({
        to: "/projects/$projectId/$audioId/auditions",
        params: { audioId, projectId },
      });
    } catch (error) {
      if (!isAbortOrCanceledError(error)) {
        toast.error("Unable to continue, please try again later");
      }
      setIsContinueClicked(false);
    }
  }, [
    currentResult?.state,
    trimmableRegions,
    saveClipRanges,
    audioId,
    navigate,
    projectId,
    currentResult?.result,
  ]);

  const handleRegionsChange = (regions: TrimmableRegion[]) => {
    setTrimmableRegions(regions);
    latestTrimmableRegions.current = regions;
    if (currentResult?.state === "done") {
      // assign words to regions using left-biased approach
      const transcriptionItems = mapWordsToRanges(regions, currentResult.result.wordTimestamps);
      setGroupedTranscription(transcriptionItems);
    }
  };

  const handleCommitRegions = () => {
    if (currentResult?.state !== "done") {
      return;
    }
    // trigger persisting the clip ranges when pointer released on regions
    saveClipRanges();
    AdjustAudioClipEvents.adjusted.track({
      project_id: projectId,
      audio_id: audioId,
      clip_count: trimmableRegions.length,
      audio_duration_ms: currentResult?.result.audioDurationMs,
    });
  };

  const handleClickTranscriptItem = (index: number) => {
    const item = groupedTranscription[index];
    if (item) {
      audioProcessorRef.current?.seek(item.startMs);
    }
  };

  useOnMountEffect(() => {
    begin({ params: { operationId: audioId } });
    audioProcessorRef.current = new AudioProcessor();
  });

  // Effect to handle setting transcription items when we have ranges and word timestamps
  useOnChangeEffect(currentResult?.result?.state, (state) => {
    if (
      state === "COMPLETE" &&
      currentResult?.result &&
      "defaultClipRanges" in currentResult.result
    ) {
      const ranges = currentResult.result.defaultClipRanges;
      const transcriptionItems = mapWordsToRanges(ranges, currentResult.result.wordTimestamps);
      setGroupedTranscription(transcriptionItems);
    }
  });

  // Effect to handle setting trimmable regions when we have ranges
  useOnChangeEffect(currentResult?.result?.state, (state) => {
    if (
      state === "COMPLETE" &&
      currentResult?.result &&
      "defaultClipRanges" in currentResult.result
    ) {
      const ranges = currentResult.result.defaultClipRanges;
      const regions = ranges.map((range: { startMs: number; endMs: number }, index: number) => ({
        startMs: range.startMs,
        endMs: range.endMs,
        index,
        id: TrimmableRegion_Id(nanoid()),
      }));
      setTrimmableRegions(regions);
    }
  });

  const audioUrl = currentResult?.result.state === "COMPLETE" && currentResult?.result.audioUrl;
  const isContinuePending = isSettingClipRanges && isContinueClicked;

  const showErrorDialog = currentResult?.state === "failed";

  return (
    <Layout projectId={projectId}>
      {!showErrorDialog && (
        <div className={containerStyles}>
          <Transcription
            items={groupedTranscription}
            currentTimeMs={currentTimeMs}
            isPending={isPollingClipRangeCreation}
            handleClickTranscriptItem={handleClickTranscriptItem}
          />

          <footer className={footerStyles}>
            <AudioTrimmer
              audio={audioUrl || undefined}
              audioProcessor={audioProcessorRef.current}
              onTimeUpdate={setCurrentTimeMs}
              minRegionSizeMs={MIN_REGION_SIZE_MS}
              maxRegionSizeMs={MAX_REGION_SIZE_MS}
              onRegionsChange={handleRegionsChange}
              onCommitRegions={handleCommitRegions}
              initialRegions={trimmableRegions}
              trimMode="regions"
              playButtonVariant="bottom"
              disabled={isContinuePending}
              isPending={isPollingClipRangeCreation}
              minErrorTip={t("mirage-studio:projects.audio.adjust.min-error-tip", {
                min: `${Math.floor(MIN_REGION_SIZE_MS / 1000)}s`,
              })}
              maxErrorTip={t("mirage-studio:projects.audio.adjust.max-error-tip", {
                max: `${Math.floor(MAX_REGION_SIZE_MS / 1000)}s`,
              })}
              defaultTip={t("mirage-studio:projects.audio.adjust.drag-tip")}
              bottom={
                <div className={actionsStyles}>
                  <Button
                    variant="primary"
                    size="md"
                    icon={<Icon name="right-arrow-small" />}
                    iconPosition="after"
                    isPending={isContinuePending}
                    onClick={onContinue}
                    disabled={!audioUrl || currentResult?.state !== "done"}
                  >
                    {t("common:continue")}
                  </Button>
                </div>
              }
            />
          </footer>
        </div>
      )}

      <Dialog.Root open={showErrorDialog}>
        <Dialog.Content
          className={errorDialogContentStyles}
          closeOnEscapeKey={false}
          closeOnOutsideClick={false}
        >
          <div className={errorDialogHeaderStyles}>
            <Icon name="error" size={32} className={css({ color: "$error" })} />
            <div className={errorDialogInfoContentStyles}>
              <Dialog.Title className={css({ textStyle: "heading-4", color: "$error" })}>
                {currentResult?.result && "error" in currentResult.result
                  ? currentResult.result.error.message
                  : "An error occurred"}
              </Dialog.Title>
              <Dialog.Description
                className={css({ textStyle: "body-2-subtle", color: "$gray.500" })}
              >
                {t("mirage-studio:projects.audio.adjust.error-description")}
              </Dialog.Description>
            </div>
          </div>
          <LinkButton
            to="/projects/$projectId"
            variant="primary"
            size="md"
            icon={<Icon name="replace" />}
          >
            {t("mirage-studio:projects.audio.adjust.replace-audio")}
          </LinkButton>
        </Dialog.Content>
      </Dialog.Root>
    </Layout>
  );
}

function Layout({ projectId, children }: { projectId: string; children: React.ReactNode }) {
  const { fromProject } = Route.useSearch();
  const { enabled: enableProjectLayout } = useFeatureFlag("studio_enable_project_nav_v2");

  const { data: projectDetails } = getProjectDetailsQuery.useQuery(
    { params: { id: projectId } },
    { staleTime: Infinity }
  );

  if (enableProjectLayout) {
    return (
      <ProjectLayout
        project={projectDetails?.project}
        subtitle={t("mirage-studio:projects.audio.adjust.subtitle-v2")}
        rightActions={<ProjectOptionsMenu projectId={projectId} />}
      >
        {children}
      </ProjectLayout>
    );
  }

  return (
    <LayoutBleed
      title={t("mirage-studio:projects.audio.adjust.title")}
      subtitle={!fromProject && t("mirage-studio:projects.audio.adjust.subtitle")}
      leftActions={
        <LinkButton
          to={fromProject ? "/projects/$projectId" : "/home"}
          variant="secondary"
          size="md"
          iconPosition="only"
          icon={<Icon name="left-arrow-small" />}
        />
      }
      rightActions={<ProjectOptionsMenu projectId={projectId} />}
    >
      {children}
    </LayoutBleed>
  );
}

interface TranscriptionItem {
  startMs: number;
  endMs: number;
  text: string;
}

interface TranscriptionProps {
  items: TranscriptionItem[];
  currentTimeMs: number;
  isPending: boolean;
  handleClickTranscriptItem: (index: number) => void;
}

interface TranscriptionListItemProps {
  item: TranscriptionItem;
  active: boolean;
  onClick: () => void;
}

function Transcription({
  items,
  currentTimeMs,
  isPending,
  handleClickTranscriptItem,
}: TranscriptionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useOnChangeEffect(currentTimeMs, () => {
    const currentItemIndex = items.findIndex(
      (item) => currentTimeMs >= item.startMs && currentTimeMs < item.endMs
    );
    if (currentItemIndex < 0) {
      return;
    }
    const container = containerRef.current;
    const clipElement = itemRefs.current[currentItemIndex];

    if (clipElement && container) {
      const containerRect = container.getBoundingClientRect();
      const clipRect = clipElement.getBoundingClientRect();

      const isAbove = clipRect.top < containerRect.top;
      const isBelow = clipRect.bottom > containerRect.bottom;

      if (isAbove || isBelow) {
        clipElement.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  });

  return (
    <ScrollArea ref={containerRef}>
      <div className={transcriptionStyles}>
        {items.map((item, index) => {
          const active = !isPending && currentTimeMs >= item.startMs && currentTimeMs < item.endMs;
          return (
            <TranscriptionListItem
              key={item.startMs}
              item={item}
              ref={(el) => (itemRefs.current[index] = el)}
              active={active}
              onClick={() => handleClickTranscriptItem(index)}
            />
          );
        })}
      </div>
    </ScrollArea>
  );
}

const TranscriptionListItem = forwardRef<HTMLButtonElement, TranscriptionListItemProps>(
  function TranscriptionListItem({ item, active, onClick }, ref) {
    return (
      <button
        ref={ref}
        className={transcriptionItemStyles({
          active,
        })}
        onClick={onClick}
      >
        <p
          className={css({
            textStyle: "body-3-subtle",
            color: "$gray.500",
            fontVariantNumeric: "tabular-nums",
          })}
        >
          {formatTimeMs(item.startMs)}
        </p>
        <p className={transcriptTextItem({ active })}>
          {item.text.length > 0
            ? item.text
            : `[${t("mirage-studio:projects.audio.adjust.silence")}]`}
        </p>
      </button>
    );
  }
);

const containerStyles = css({
  flex: 1,
  display: "grid",
  gridTemplateRows: "1fr auto",
  gap: "16px",
  width: "100%",
  maxWidth: "650px",
  margin: "50px auto",
  height: "100%",
  minHeight: 0,
});

const errorDialogContentStyles = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "32px",
  padding: "48px 24px",
  width: "430px",
  textAlign: "center",
  color: "$error",
});

const errorDialogHeaderStyles = css({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "24px",
});

const errorDialogInfoContentStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "8px",
});

const transcriptionStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "4px",
  minHeight: 0,
});

const transcriptionItemStyles = cva({
  base: {
    display: "flex",
    textAlign: "left",
    flexDirection: "column",
    gap: "4px",
    borderRadius: "16px",
    padding: "12px",
    width: "100%",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "$gray.900",
    },
  },
  variants: {
    active: {
      true: {
        backgroundColor: "$gray.900",
        textStyle: "body-2-intense",
      },
      false: {
        textStyle: "body-2-subtle",
      },
    },
  },
});

const footerStyles = css({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
});

const actionsStyles = css({
  display: "flex",
  justifyContent: "end",
  alignItems: "center",
});

const transcriptTextItem = cva({
  base: {},
  variants: {
    active: {
      true: {
        color: "$sunny",
      },
      false: {
        color: "$gray.200",
      },
    },
  },
});

/**
 * Assigns words to ranges ensuring:
 * 1. No words are dropped
 * 2. Words don't appear in multiple ranges
 * 3. Words that overlap boundaries belong to the previous (left) range
 */
const mapWordsToRanges = (
  ranges: { startMs: number; endMs: number }[],
  wordTimestamps: { startMs: number; endMs: number; text: string }[]
): TranscriptionItem[] => {
  // sort ranges by start time
  const sortedRanges = [...ranges].sort((a, b) => a.startMs - b.startMs);

  const result = sortedRanges.map((range) => ({
    startMs: range.startMs,
    endMs: range.endMs,
    words: [] as { startMs: number; endMs: number; text: string }[],
  }));

  // assign each word to exactly one range
  for (const word of wordTimestamps) {
    let assigned = false;

    // first, try to find a range where the word is completely contained
    for (let i = 0; i < sortedRanges.length; i++) {
      const range = sortedRanges[i];
      if (word.startMs >= range.startMs && word.endMs <= range.endMs) {
        result[i].words.push(word);
        assigned = true;
        break;
      }
    }

    // if not assigned yet, assign to the leftmost overlapping range
    if (!assigned) {
      for (let i = 0; i < sortedRanges.length; i++) {
        const range = sortedRanges[i];
        // Check if word overlaps with this range
        if (word.endMs > range.startMs && word.startMs < range.endMs) {
          result[i].words.push(word);
          assigned = true;
          break;
        }
      }
    }

    // if still not assigned and there are ranges, assign to the closest range
    if (!assigned && sortedRanges.length > 0) {
      let closestRangeIndex = 0;
      let minDistance = Number.MAX_SAFE_INTEGER;

      for (let i = 0; i < sortedRanges.length; i++) {
        const range = sortedRanges[i];
        const distToStart = Math.abs(word.startMs - range.startMs);
        const distToEnd = Math.abs(word.endMs - range.endMs);
        const distance = Math.min(distToStart, distToEnd);

        if (distance < minDistance) {
          minDistance = distance;
          closestRangeIndex = i;
        }
      }

      result[closestRangeIndex].words.push(word);
    }
  }

  // Convert result to transcription items
  return result.map((item) => ({
    startMs: item.startMs,
    endMs: item.endMs,
    text:
      item.words.length > 0
        ? item.words
            .sort((a, b) => a.startMs - b.startMs)
            .map((word) => word.text)
            .join(" ")
        : "",
  }));
};
